import { Alert } from "@mui/material";

import { useAnalytics } from "@/providers/analytics";
import {
  MetadataForm,
  MetadataView,
  type Metadata,
  type MetadataDiff,
} from "@/shared/components/Metadata";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { type SessionRecord } from "@/shared/domain/sessions";
import { useUpdateSession } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./SessionMetadata.module.css";

interface SessionMetadataProps {
  session: SessionRecord;
}

interface ExistingMetadataProps {
  metadata: Metadata;
  sessionKey: string;
  disabled: boolean;
  onChange: (next: Metadata, diff?: MetadataDiff) => void;
  onDelete: (key: string) => void;
}

function ExistingMetadata({
  metadata,
  sessionKey,
  disabled,
  onChange,
  onDelete,
}: ExistingMetadataProps) {
  if (Object.keys(metadata).length === 0) {
    return null;
  }
  return (
    <MetadataView
      key={sessionKey}
      className={styles.metadataView}
      metadata={metadata}
      onChange={onChange}
      onDeleteMetadata={onDelete}
      disabled={disabled}
    />
  );
}

function MutationErrorAlert({ error }: { error: Error | null }) {
  if (!error) {
    return null;
  }
  return (
    <Alert severity="error" className={styles.inlineError}>
      {userFacingErrorMessage(error, "Failed to update session metadata.")}
    </Alert>
  );
}

export function SessionMetadata({ session }: SessionMetadataProps) {
  const { trackEvent } = useAnalytics();
  const { mutateAsync: updateSession, error, isPending } = useUpdateSession();

  async function handleAddMetadata(key: string, value: unknown) {
    trackEvent("AddSessionMetadataClicked", { sessionId: session.session_id });
    try {
      await updateSession({
        sessionId: session.session_id,
        resourceOwnerId: session.org_id,
        updates: { metadata_changeset: { put_fields: { [key]: value } } },
      });
    } catch {
      // Surfaced via mutation.error → MutationErrorAlert below.
    }
  }

  async function handleRemoveMetadata(key: string) {
    trackEvent("RemoveSessionMetadataClicked", {
      sessionId: session.session_id,
    });
    try {
      await updateSession({
        sessionId: session.session_id,
        resourceOwnerId: session.org_id,
        updates: { metadata_changeset: { remove_fields: [key] } },
      });
    } catch {
      // Surfaced via mutation.error → MutationErrorAlert below.
    }
  }

  async function handleMetadataChange(
    next: Record<string, unknown>,
    diff?: MetadataDiff,
  ) {
    // Inline JSON edits surface as `keysUpdated`; deletes are handled by
    // the dedicated `onDeleteMetadata` callback below to avoid firing two
    // mutations for the same chip-delete gesture.
    if (!diff?.keysUpdated?.length) {
      return;
    }
    const putFields: Record<string, unknown> = {};
    for (const key of diff.keysUpdated) {
      putFields[key] = next[key];
    }
    try {
      await updateSession({
        sessionId: session.session_id,
        resourceOwnerId: session.org_id,
        updates: { metadata_changeset: { put_fields: putFields } },
      });
    } catch {
      // Surfaced via mutation.error → MutationErrorAlert below.
    }
  }

  return (
    <div className={styles.container}>
      <ExistingMetadata
        metadata={session.metadata}
        sessionKey={session.session_id}
        disabled={isPending}
        onChange={(next, diff) => void handleMetadataChange(next, diff)}
        onDelete={(key) => void handleRemoveMetadata(key)}
      />
      <MetadataForm
        key={`${session.session_id}-form`}
        className={styles.metadataForm}
        metadata={session.metadata}
        autocompleteType={AutocompleteType.SessionMetadataKeys}
        onAddMetadata={(key, value) => void handleAddMetadata(key, value)}
        disabled={isPending}
        isLoading={isPending}
      />
      <MutationErrorAlert error={error} />
    </div>
  );
}
